/**
 * The charts themselves.
 *
 * Five forms, and the list is deliberately short — a reports section with nine chart types is a
 * reports section nobody can read at a glance, because every screen has to be learned again.
 *
 * | Form | The job it does |
 * |---|---|
 * | `ColumnChart` | a measure over time, one or two series, grouped or stacked |
 * | `TrendLine` | one measure over time where the *shape* is the point — a rate, a percentage |
 * | `CompositionBar` | one total broken into ordered parts, side by side, direct-labelled |
 * | `RankedBars` | many named things by size, biggest first |
 * | `Donut` | one total as a share, when there are few enough parts to name them |
 * | `Sparkline` | the shape of a series beside a figure that is already written out |
 *
 * Everything here is presentation. **Not one of these components adds up a column, works out a
 * percentage or decides what a total is** — every number arrives computed, from the endpoint that
 * also computed the number printed in the table beside it. That is the rule that keeps a chart
 * from disagreeing with its own figures, which is the commonest fault in a dashboard and the one
 * hardest to notice, because both halves look plausible on their own.
 *
 * Read `kit.tsx` first: the palette, the textures, the measurement hook and `ChartFrame` — which
 * is what gives every chart below its legend and its figures table — all live there.
 */

import { type ReactNode } from "react";
import {
  AGEING_SCALE,
  CHROME,
  ChartFrame,
  ChartTextures,
  OTHER,
  SERIES,
  alternatingTextures,
  fillFor,
  labelEvery,
  niceCeiling,
  ticksTo,
  useChartId,
  visibleHeight,
  type FigureTable,
  type LegendEntry,
  type Texture,
} from "./kit";

export { AGEING_SCALE, CHROME, OTHER, SEGMENT_COLOURS, SERIES } from "./kit";
export type { Texture } from "./kit";

/**
 * Gridlines whose labels are not the same number twice.
 *
 * Four evenly spaced ticks up to a ceiling of two gives 0, 0.5, 1, 1.5, 2 — and a chart of counts,
 * which has no half of anything, formats that as "0, 1, 1, 2, 2". Two lines a hair apart carrying
 * the same figure is worse than one line: a reader looking at "1" twice has to work out which is
 * lying. So a tick whose label repeats the one below it is dropped, which leaves an integer axis
 * with integer gridlines and changes nothing on an axis of money.
 */
function distinctTicks(ticks: number[], format: (value: number) => string): number[] {
  const kept: number[] = [];
  let previous: string | null = null;
  for (const tick of ticks) {
    const label = tick === 0 ? "0" : format(tick);
    if (label === previous) continue;
    previous = label;
    kept.push(tick);
  }
  return kept;
}

/* ============================================================ column chart */

export interface ColumnSeries {
  label: string;
  colour: string;
  texture?: Texture;
}

export interface ColumnPoint {
  /** The axis label. Short — "Sep", "wk 3". */
  label: string;
  /** The full name, for the figures table and the accessible summary. */
  full: string;
  values: number[];
}

/**
 * A measure over time, as columns.
 *
 * **Grouped** when two series are being compared — billed against collected, where the reader's
 * question is "how far apart are they this month", and stacking would hide exactly that.
 * **Stacked** when the series are parts of one total — three kinds of appointment in a week, where
 * the reader's question is "how busy is that week".
 *
 * The second series in a grouped pair is textured as well as coloured. Two greens of different
 * saturation is a chart that vanishes on a monochrome printer and for one reader in twelve.
 */
export function ColumnChart({
  label,
  points,
  series,
  mode = "grouped",
  format,
  height = 220,
  footnote,
  highlight = 0,
}: {
  label: string;
  points: ColumnPoint[];
  series: ColumnSeries[];
  mode?: "grouped" | "stacked";
  format: (value: number) => string;
  height?: number;
  footnote?: ReactNode;
  /** Which series gets the one direct label, on its largest column. -1 for none. */
  highlight?: number;
}) {
  const prefix = useChartId();

  const figures: FigureTable = {
    caption: label,
    columns: ["Period", ...series.map((s) => s.label)],
    rows: points.map((point) => [point.full, ...point.values.map(format)]),
  };

  const legend: LegendEntry[] = series.map((s) => ({ label: s.label, colour: s.colour, texture: s.texture }));

  const ceiling = niceCeiling(
    Math.max(
      1,
      ...points.map((point) => (mode === "stacked" ? point.values.reduce((a, b) => a + b, 0) : Math.max(...point.values))),
    ),
  );

  /* The one direct label: the biggest column of the highlighted series. One number on a chart is a
     caption; a number on every column is a table drawn badly. */
  const peakIndex =
    highlight >= 0 && points.length
      ? points.reduce((best, point, index) => (point.values[highlight] > points[best].values[highlight] ? index : best), 0)
      : -1;

  return (
    <ChartFrame label={label} legend={legend} figures={figures} footnote={footnote} series={series.length}>
      {(width) => {
        const padLeft = 52;
        const padRight = 10;
        const padTop = 18;
        const padBottom = 22;
        const plotWidth = Math.max(10, width - padLeft - padRight);
        const plotHeight = Math.max(10, height - padTop - padBottom);
        const slot = plotWidth / Math.max(1, points.length);
        const scale = plotHeight / ceiling;
        const showLabel = labelEvery(points.length, plotWidth);
        /* A 2px gap between adjacent fills, so two bars of the same colour never read as one wide
           one. Taken out of the bar rather than added between slots, so the slots stay even. */
        const barGap = 2;
        const groupWidth = Math.min(slot - 6, mode === "stacked" ? 26 : 34);
        const barWidth = mode === "stacked" ? groupWidth : (groupWidth - barGap * (series.length - 1)) / series.length;

        return (
          <svg width={width} height={height} aria-hidden="true" className="overflow-visible">
            <ChartTextures prefix={prefix} colours={series.map((s) => s.colour)} />

            {distinctTicks(ticksTo(ceiling), format).map((tick) => {
              const y = padTop + plotHeight - tick * scale;
              return (
                <g key={tick}>
                  <line x1={padLeft} y1={y} x2={padLeft + plotWidth} y2={y} stroke={CHROME.grid} strokeWidth="1" />
                  <text x={padLeft - 8} y={y + 3.5} textAnchor="end" fontSize="10" fill={CHROME.muted}>
                    {/* A bare nought at the baseline. `formatMoney` renders zero as the currency
                        with two decimals, which is right on an invoice and noise on an axis. */}
                    {tick === 0 ? "0" : format(tick)}
                  </text>
                </g>
              );
            })}

            {points.map((point, index) => {
              const centre = padLeft + slot * index + slot / 2;
              /* Where each segment of this column starts and how tall it is, worked out before
                 anything is drawn. A stack computed inside the render — subtracting from a
                 running total as the marks are emitted — is how a re-render in a different order
                 produces a chart that is subtly wrong and looks fine. */
              const laid = series.map((s, seriesIndex) => ({
                series: s,
                seriesIndex,
                height: visibleHeight(point.values[seriesIndex] ?? 0, scale),
              }));
              let stackTop = padTop + plotHeight;
              const placed = laid.map((bar) => {
                if (mode === "stacked") {
                  const y = stackTop - bar.height;
                  if (bar.height > 0) stackTop = y - barGap;
                  return { ...bar, x: centre - groupWidth / 2, y };
                }
                return {
                  ...bar,
                  x: centre - groupWidth / 2 + bar.seriesIndex * (barWidth + barGap),
                  y: padTop + plotHeight - bar.height,
                };
              });

              return (
                <g key={point.full}>
                  {placed.map((bar) =>
                    bar.height <= 0 ? null : (
                      <rect
                        key={bar.series.label}
                        x={bar.x}
                        y={bar.y}
                        width={Math.max(2, barWidth)}
                        height={bar.height}
                        rx="3"
                        fill={fillFor(prefix, bar.seriesIndex, bar.series.colour, bar.series.texture ?? "solid")}
                      />
                    ),
                  )}

                  {index === peakIndex && point.values[highlight] > 0 ? (
                    <text
                      x={centre}
                      y={padTop + plotHeight - visibleHeight(point.values[highlight], scale) - 6}
                      textAnchor="middle"
                      fontSize="10.5"
                      fontWeight="600"
                      fill={CHROME.ink}
                    >
                      {format(point.values[highlight])}
                    </text>
                  ) : null}

                  {showLabel(index) ? (
                    <text x={centre} y={height - 6} textAnchor="middle" fontSize="10" fill={CHROME.muted}>
                      {point.label}
                    </text>
                  ) : null}
                </g>
              );
            })}

            <line
              x1={padLeft}
              y1={padTop + plotHeight}
              x2={padLeft + plotWidth}
              y2={padTop + plotHeight}
              stroke={CHROME.axis}
              strokeWidth="1"
            />
          </svg>
        );
      }}
    </ChartFrame>
  );
}

/* =============================================================== trend line */

/**
 * One measure over time, where the shape is the point.
 *
 * A rate rather than an amount: occupancy, collection rate — figures whose absolute value matters
 * less than whether they are going up or down. Drawn as a line with a faint area beneath it, a
 * marker on every point so the readings are locatable rather than implied, and a direct label on
 * the last one, which is the value somebody is actually looking for.
 *
 * **The axis starts at zero.** A rate axis cropped to its own range turns three points of drift
 * into a cliff, which is the single most effective way to mislead with a chart and the reason the
 * ceiling here is not derived from the minimum.
 */
export function TrendLine({
  label,
  points,
  format,
  ceiling,
  height = 200,
  colour = SERIES[0],
  footnote,
  valueColumn = "Value",
}: {
  label: string;
  points: Array<{ label: string; full: string; value: number; detail?: string }>;
  format: (value: number) => string;
  /** Fixed top of the axis, for a percentage. Omitted, it is derived from the data. */
  ceiling?: number;
  height?: number;
  colour?: string;
  footnote?: ReactNode;
  valueColumn?: string;
}) {
  const figures: FigureTable = {
    caption: label,
    columns: points.some((p) => p.detail) ? ["Period", valueColumn, "Of"] : ["Period", valueColumn],
    rows: points.map((point) =>
      point.detail ? [point.full, format(point.value), point.detail] : [point.full, format(point.value)],
    ),
  };

  const top = ceiling ?? niceCeiling(Math.max(1, ...points.map((p) => p.value)));

  return (
    <ChartFrame label={label} figures={figures} footnote={footnote}>
      {(width) => {
        const padLeft = 44;
        const padRight = 44;
        const padTop = 14;
        const padBottom = 22;
        const plotWidth = Math.max(10, width - padLeft - padRight);
        const plotHeight = Math.max(10, height - padTop - padBottom);
        const stepX = points.length > 1 ? plotWidth / (points.length - 1) : 0;
        const xOf = (index: number) => padLeft + stepX * index;
        const yOf = (value: number) => padTop + plotHeight - (Math.min(value, top) / top) * plotHeight;
        const showLabel = labelEvery(points.length, plotWidth);
        const line = points.map((point, index) => `${index === 0 ? "M" : "L"}${xOf(index)},${yOf(point.value)}`).join(" ");
        const area = `${line} L${xOf(points.length - 1)},${padTop + plotHeight} L${xOf(0)},${padTop + plotHeight} Z`;
        const last = points[points.length - 1];

        return (
          <svg width={width} height={height} aria-hidden="true" className="overflow-visible">
            {distinctTicks(ticksTo(top), format).map((tick) => {
              const y = yOf(tick);
              return (
                <g key={tick}>
                  <line x1={padLeft} y1={y} x2={padLeft + plotWidth} y2={y} stroke={CHROME.grid} strokeWidth="1" />
                  <text x={padLeft - 8} y={y + 3.5} textAnchor="end" fontSize="10" fill={CHROME.muted}>
                    {/* A bare nought at the baseline. `formatMoney` renders zero as the currency
                        with two decimals, which is right on an invoice and noise on an axis. */}
                    {tick === 0 ? "0" : format(tick)}
                  </text>
                </g>
              );
            })}

            {points.length > 1 ? <path d={area} fill={colour} fillOpacity="0.1" /> : null}
            {points.length > 1 ? <path d={line} fill="none" stroke={colour} strokeWidth="2" strokeLinejoin="round" /> : null}

            {points.map((point, index) => (
              <g key={point.full}>
                {/* A 2px ring in the surface colour, so a marker landing on the line beneath it
                    still reads as a separate mark rather than a thickening. */}
                <circle cx={xOf(index)} cy={yOf(point.value)} r="4.5" fill="#ffffff" />
                <circle cx={xOf(index)} cy={yOf(point.value)} r="3" fill={colour} />
                {showLabel(index) ? (
                  <text x={xOf(index)} y={height - 6} textAnchor="middle" fontSize="10" fill={CHROME.muted}>
                    {point.label}
                  </text>
                ) : null}
              </g>
            ))}

            {last ? (
              <text x={xOf(points.length - 1) + 8} y={yOf(last.value) + 3.5} fontSize="11" fontWeight="600" fill={CHROME.ink}>
                {format(last.value)}
              </text>
            ) : null}
          </svg>
        );
      }}
    </ChartFrame>
  );
}

/* ========================================================== composition bar */

export interface Segment {
  label: string;
  value: number;
  colour: string;
  texture?: Texture;
}

/**
 * One total, broken into its parts, on a single horizontal bar.
 *
 * The right form for an ordered decomposition — arrears by age, collected split into what the
 * agency keeps and what the owner gets, released split into sold and still going. A donut of the
 * same numbers would be harder to compare and impossible to label; a stacked column of one column
 * is a bar drawn vertically for no reason.
 *
 * **Every segment is labelled underneath, whatever its width.** A segment three pixels wide is a
 * real figure and is exactly the one a reader most wants named, so the names live in a row beneath
 * the bar rather than inside it — inside, they would be dropped for the narrow segments, which is
 * the same as saying only the big numbers matter.
 */
export function CompositionBar({
  label,
  segments,
  format,
  footnote,
  total,
}: {
  label: string;
  segments: Segment[];
  format: (value: number) => string;
  footnote?: ReactNode;
  /** The whole the parts are of. Defaults to their sum, which is right for a decomposition. */
  total?: number;
}) {
  const prefix = useChartId();
  const sum = total ?? segments.reduce((acc, segment) => acc + segment.value, 0);
  const share = (value: number) => (sum > 0 ? Math.round((value / sum) * 100) : 0);
  /* The second channel, decided here rather than by the caller — see `alternatingTextures`. A part
     that is empty today would otherwise take its texture out of the bar with it. */
  const textures = alternatingTextures(segments.map((segment) => segment.value));

  const figures: FigureTable = {
    caption: label,
    columns: ["Part", "Amount", "Share"],
    rows: segments.map((segment) => [segment.label, format(segment.value), `${share(segment.value)}%`]),
  };

  return (
    <ChartFrame
      label={label}
      figures={figures}
      footnote={footnote}
      series={segments.filter((segment) => segment.value > 0).length}
    >
      {(width) => {
        const height = 14;
        const gap = 2;
        const drawable = segments.filter((segment) => segment.value > 0);
        const track = Math.max(10, width - gap * Math.max(0, drawable.length - 1));
        let x = 0;

        return (
          <svg width={width} height={height} aria-hidden="true">
            <ChartTextures prefix={prefix} colours={segments.map((segment) => segment.colour)} />
            <rect width={width} height={height} rx="7" fill={CHROME.track} />
            {segments.map((segment, index) => {
              if (segment.value <= 0) return null;
              const segmentWidth = sum > 0 ? Math.max(3, (segment.value / sum) * track) : 0;
              const at = x;
              x += segmentWidth + gap;
              return (
                <rect
                  key={segment.label}
                  x={at}
                  y="0"
                  width={segmentWidth}
                  height={height}
                  rx="4"
                  fill={fillFor(prefix, index, segment.colour, textures[index])}
                />
              );
            })}
          </svg>
        );
      }}
    </ChartFrame>
  );
}

/**
 * The names and figures under a composition bar.
 *
 * Kept out of `CompositionBar` itself so a caller can put it where the layout wants it — beside
 * the bar on a wide card, beneath it on a narrow one — and so the same list can caption a donut.
 */
export function SegmentKey({ segments, format, total }: { segments: Segment[]; format: (value: number) => string; total?: number }) {
  const prefix = useChartId();
  const sum = total ?? segments.reduce((acc, segment) => acc + segment.value, 0);
  /* The same rule as the bar, from the same function, over the same array — a key whose swatches
     did not match the bar's fills would be a legend for a different chart. */
  const textures = alternatingTextures(segments.map((segment) => segment.value));

  /* One column, not two. A two-column grid fills row-wise, so an ordered list of three parts
     reads first, third, second — which is worse than a slightly taller card. */
  return (
    <ul className="mt-3 space-y-2">
      {segments.map((segment, index) => (
        <li key={segment.label} className="flex items-baseline gap-2">
          {/* One texture namespace per row — see the note in `Legend`. A prefix shared down the
              list defines the same pattern id several times and every textured swatch takes the
              first one's colour. */}
          <svg width="10" height="10" aria-hidden="true" className="mt-1 shrink-0">
            <ChartTextures prefix={`${prefix}-key-${index}`} colours={[segment.colour]} />
            <rect
              width="10"
              height="10"
              rx="2"
              fill={fillFor(`${prefix}-key-${index}`, 0, segment.colour, textures[index])}
            />
          </svg>
          <span className="min-w-0 flex-1 text-xs text-ink-600">{segment.label}</span>
          <span className="tabular text-xs font-medium text-ink-800">{format(segment.value)}</span>
          <span className="tabular w-9 text-right text-xs text-ink-400">
            {sum > 0 ? Math.round((segment.value / sum) * 100) : 0}%
          </span>
        </li>
      ))}
    </ul>
  );
}

/* =============================================================== ranked bars */

/**
 * Named things by size, biggest first.
 *
 * One colour for every row, because identity is carried by the row's own name sitting right beside
 * it — giving each row its own hue would be colour with nothing to say, and would run out at the
 * fifth row. The bar is a comparison of lengths and nothing else.
 */
export function RankedBars({
  label,
  rows,
  format,
  colour = SERIES[0],
  footnote,
  valueColumn = "Amount",
  max,
}: {
  label: string;
  rows: Array<{ label: string; value: number; sub?: string }>;
  format: (value: number) => string;
  colour?: string;
  footnote?: ReactNode;
  valueColumn?: string;
  /** The length the longest bar is measured against. Defaults to the largest row. */
  max?: number;
}) {
  const ceiling = Math.max(1, max ?? Math.max(0, ...rows.map((row) => row.value)));

  const figures: FigureTable = {
    caption: label,
    columns: ["Name", valueColumn],
    rows: rows.map((row) => [row.sub ? `${row.label} — ${row.sub}` : row.label, format(row.value)]),
  };

  return (
    <ChartFrame label={label} figures={figures} footnote={footnote}>
      {() => (
        <ul className="space-y-2.5">
          {rows.map((row) => (
            <li key={`${row.label}-${row.sub ?? ""}`}>
              <div className="flex items-baseline justify-between gap-3 text-xs">
                <span className="min-w-0 truncate text-ink-700">
                  {row.label}
                  {row.sub ? <span className="ml-1.5 text-ink-400">{row.sub}</span> : null}
                </span>
                <span className="tabular shrink-0 font-medium text-ink-800">{format(row.value)}</span>
              </div>
              <div className="mt-1 h-2 w-full overflow-hidden rounded-full" style={{ background: CHROME.track }}>
                <div
                  className="h-full rounded-full"
                  style={{ width: `${Math.max(row.value > 0 ? 2 : 0, (row.value / ceiling) * 100)}%`, background: colour }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}
    </ChartFrame>
  );
}

/* ==================================================================== donut */

/**
 * A total as a share, when there are few enough parts to name every one of them.
 *
 * Used for one thing in these reports — which ways money actually arrives — because that is a
 * genuine part-to-whole with a handful of parts and no order to it. Anything ordered goes to
 * `CompositionBar`, and anything with more than five parts goes to `RankedBars`, where the fifth
 * onwards fold into a neutral "Other" rather than growing a fifth hue.
 *
 * The hole in the middle is not decoration: it holds the total, which is the figure a reader wants
 * before they want any of the shares.
 */
export function Donut({
  label,
  slices,
  format,
  centre,
  centreLabel,
  footnote,
  size = 170,
}: {
  label: string;
  slices: Array<{ label: string; value: number }>;
  format: (value: number) => string;
  centre: string;
  centreLabel: string;
  footnote?: ReactNode;
  size?: number;
}) {
  const prefix = useChartId();
  const total = slices.reduce((sum, slice) => sum + slice.value, 0);
  /* Four hues and then a neutral, never a fifth hue — see the note on `SERIES`. The fifth slot is
     "Other" by construction, so a caller that hands over six slices gets five drawn parts and the
     sixth folded in; the figures table still lists every one of them. */
  /*
    Four hues, and every other one textured.

    The palette's own checks put the worst pair of these four at a tritan separation of about six,
    which is inside the band that is only legal **with** a second channel — so the second channel is
    here rather than argued about. Alternating means no two neighbouring arcs are ever the same
    colour *and* the same fill, which is the pair a reader has to tell apart. The fifth slot onwards
    is the neutral "Other" and takes the third texture, so it never reads as a fifth hue.
  */
  const coloured = slices.map((slice, index) => ({
    ...slice,
    colour: index < SERIES.length ? SERIES[index] : OTHER,
    texture: (index >= SERIES.length ? "grid" : index % 2 === 1 ? "hatch" : "solid") as Texture,
  }));

  const figures: FigureTable = {
    caption: label,
    columns: ["Part", "Amount", "Share"],
    rows: slices.map((slice) => [
      slice.label,
      format(slice.value),
      `${total > 0 ? Math.round((slice.value / total) * 100) : 0}%`,
    ]),
  };

  return (
    <ChartFrame
      label={label}
      figures={figures}
      footnote={footnote}
      series={slices.length}
      legend={coloured.map((slice) => ({
        label: slice.label,
        colour: slice.colour,
        texture: slice.texture,
        value: `${total > 0 ? Math.round((slice.value / total) * 100) : 0}%`,
      }))}
    >
      {() => {
        const radius = size / 2;
        const stroke = 26;
        const inner = radius - stroke / 2;
        const circumference = 2 * Math.PI * inner;
        let offset = 0;

        return (
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true" className="mx-auto block">
            <ChartTextures prefix={prefix} colours={coloured.map((slice) => slice.colour)} />
            <circle cx={radius} cy={radius} r={inner} fill="none" stroke={CHROME.track} strokeWidth={stroke} />
            {coloured.map((slice, index) => {
              if (slice.value <= 0 || total <= 0) return null;
              const length = (slice.value / total) * circumference;
              /* A 2px gap between neighbouring arcs, drawn in the surface colour, so two
                 adjacent slices never merge into one. */
              const dash = `${Math.max(0, length - 2)} ${circumference - Math.max(0, length - 2)}`;
              const at = offset;
              offset += length;
              return (
                <circle
                  key={slice.label}
                  cx={radius}
                  cy={radius}
                  r={inner}
                  fill="none"
                  stroke={fillFor(prefix, index, slice.colour, slice.texture)}
                  strokeWidth={stroke}
                  strokeDasharray={dash}
                  strokeDashoffset={-at}
                  transform={`rotate(-90 ${radius} ${radius})`}
                />
              );
            })}
            <text x={radius} y={radius - 2} textAnchor="middle" fontSize="15" fontWeight="600" fill={CHROME.ink}>
              {centre}
            </text>
            <text x={radius} y={radius + 14} textAnchor="middle" fontSize="10" fill={CHROME.muted}>
              {centreLabel}
            </text>
          </svg>
        );
      }}
    </ChartFrame>
  );
}

/* ================================================================ sparkline */

/**
 * The shape of a series, beside a figure that is already written out.
 *
 * **This one is `aria-hidden` and carries no figures table, and that is correct rather than a
 * shortcut.** A sparkline is only ever drawn next to the number it is the history of — on the hub
 * cards, beside the total in words — so the text equivalent is the sentence it sits in. A screen
 * reader that also read out twelve unlabelled data points would be reading noise. Every chart that
 * carries information of its own goes through `ChartFrame` and gets a table.
 */
export function Sparkline({
  values,
  width = 96,
  height = 28,
  colour = SERIES[0],
  kind = "line",
}: {
  values: number[];
  width?: number;
  height?: number;
  colour?: string;
  kind?: "line" | "bars";
}) {
  if (values.length === 0) return null;
  const max = Math.max(1, ...values);

  if (kind === "bars") {
    const slot = width / values.length;
    const barWidth = Math.max(1.5, slot - 1.5);
    return (
      <svg width={width} height={height} aria-hidden="true" className="shrink-0">
        {values.map((value, index) => {
          const barHeight = value > 0 ? Math.max(1.5, (value / max) * height) : 0;
          return (
            <rect
              key={index}
              x={index * slot}
              y={height - barHeight}
              width={barWidth}
              height={barHeight}
              rx="1"
              fill={colour}
              fillOpacity={index === values.length - 1 ? 1 : 0.45}
            />
          );
        })}
      </svg>
    );
  }

  const stepX = values.length > 1 ? width / (values.length - 1) : 0;
  const path = values
    .map((value, index) => `${index === 0 ? "M" : "L"}${index * stepX},${height - (value / max) * (height - 3) - 1.5}`)
    .join(" ");

  return (
    <svg width={width} height={height} aria-hidden="true" className="shrink-0 overflow-visible">
      <path d={path} fill="none" stroke={colour} strokeWidth="1.75" strokeLinejoin="round" strokeLinecap="round" />
      <circle
        cx={(values.length - 1) * stepX}
        cy={height - (values[values.length - 1] / max) * (height - 3) - 1.5}
        r="2.5"
        fill={colour}
      />
    </svg>
  );
}

/* =================================================================== meter */

/**
 * One proportion, as a bar with its own label.
 *
 * The smallest thing in the kit and the one used most: a collection rate, an occupancy rate, how
 * much of a payment plan has arrived. It has no figures table because it has exactly one figure
 * and that figure is printed on it.
 */
export function Meter({
  value,
  max,
  label,
  caption,
  colour = SERIES[0],
}: {
  value: number;
  max: number;
  label: string;
  caption?: string;
  colour?: string;
}) {
  const pct = max <= 0 ? 0 : Math.min(100, Math.round((value / max) * 100));
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between gap-2 text-xs">
        <span className="text-ink-600">{label}</span>
        <span className="tabular font-medium text-ink-800">{pct}%</span>
      </div>
      <div
        className="h-2 w-full overflow-hidden rounded-full"
        style={{ background: CHROME.track }}
        role="meter"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label}
      >
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: colour }} />
      </div>
      {caption ? <p className="mt-1 text-xs text-ink-400">{caption}</p> : null}
    </div>
  );
}

/* =============================================================== mini stack */

/**
 * A composition bar with no frame, no legend and no figures table.
 *
 * **For one place only: a chart drawn inside something that is itself a link.** `ChartFrame`'s
 * "Show the figures" control is a `<button>`, and a button inside an anchor is invalid markup that
 * browsers resolve by breaking one of the two — so the index cards, which are links end to end,
 * cannot use it.
 *
 * The rule about a text equivalent is not waived, it is moved: a caller must print the same
 * figures beside it, which on the index cards is the run of amounts under the bar. This is
 * `aria-hidden` precisely because that line is the accessible version and reading both would be
 * reading it twice.
 */
export function MiniStack({ segments, height = 8 }: { segments: Segment[]; height?: number }) {
  const prefix = useChartId();
  const total = segments.reduce((sum, segment) => sum + segment.value, 0);
  if (total <= 0) return <div className="h-2 w-full rounded-full" style={{ background: CHROME.track }} />;

  return (
    <svg width="100%" height={height} aria-hidden="true" className="block" preserveAspectRatio="none">
      <ChartTextures prefix={prefix} colours={segments.map((segment) => segment.colour)} />
      <rect width="100%" height={height} rx={height / 2} fill={CHROME.track} />
      {(() => {
        let at = 0;
        return segments.map((segment, index) => {
          if (segment.value <= 0) return null;
          const share = (segment.value / total) * 100;
          const x = at;
          at += share;
          return (
            <rect
              key={segment.label}
              x={`${x}%`}
              y="0"
              width={`${Math.max(1.5, share - 0.6)}%`}
              height={height}
              rx={height / 2}
              fill={fillFor(prefix, index, segment.colour, segment.texture ?? "solid")}
            />
          );
        });
      })()}
    </svg>
  );
}
